import { useState, useEffect } from "react";
import axios from "axios";
import {
  X, UserPlus, Trash2, Loader2, Shield, Send, Copy, Check, Clock, RotateCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` },
});

export default function AdminUsersModal({ open, onClose, currentUserId }) {
  const [tab, setTab] = useState("admins"); // admins | invites
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "" });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, i] = await Promise.all([
        axiosAuth().get(`${API}/admin/admins`),
        axiosAuth().get(`${API}/admin/invites`),
      ]);
      setAdmins(a.data || []);
      setInvites(i.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);
  if (!open) return null;

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(link);
      setTimeout(() => setCopiedToken(null), 1500);
    } catch {
      window.prompt("Copy this invitation link:", link);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.email.includes("@")) { setError("Name and valid email required"); return; }
    setAdding(true);
    try {
      const { data } = await axiosAuth().post(`${API}/admin/invites`, form);
      setForm({ name: "", email: "" });
      setLastCreated(data);
      setTab("invites");
      await load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setError(detail || (status ? `Request failed (HTTP ${status})` : "Network error — please retry"));
    } finally { setAdding(false); }
  };

  const resendInvite = async (inviteId) => {
    try { await axiosAuth().post(`${API}/admin/invites/${inviteId}/resend`); alert("Invitation resent."); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  const revokeInvite = async (inviteId) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try { await axiosAuth().delete(`${API}/admin/invites/${inviteId}`); await load(); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  const handleDeleteAdmin = async (userId) => {
    if (!window.confirm("Remove this admin? They will lose access immediately.")) return;
    try { await axiosAuth().delete(`${API}/admin/admins/${userId}`); await load(); } catch (err) { alert(err.response?.data?.detail || "Failed to delete admin"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={onClose} data-testid="admin-users-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-zinc-700" />
            <h2 className="font-bold text-zinc-900">Admin access</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 hover:bg-zinc-100 rounded-full flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="px-6 pt-4 flex gap-1 border-b border-zinc-100">
          {[
            { k: "admins", label: "Active" },
            { k: "invites", label: `Invitations${invites.filter(i => i.status === "pending").length ? ` (${invites.filter(i => i.status === "pending").length})` : ""}` },
            { k: "new", label: "Invite someone" },
          ].map(t => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? "text-zinc-950 border-zinc-950" : "text-zinc-500 border-transparent hover:text-zinc-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 size={20} className="animate-spin text-zinc-400" /></div>
          ) : tab === "admins" ? (
            <div className="space-y-2" data-testid="admin-list">
              {admins.map(a => (
                <div key={a.user_id} data-testid={`admin-row-${a.user_id}`} className="flex items-center justify-between bg-zinc-50 rounded-xl px-4 py-3">
                  <div>
                    <div className="font-semibold text-zinc-900 text-sm">{a.name}</div>
                    <div className="text-xs text-zinc-500">{a.email}</div>
                  </div>
                  {a.user_id === currentUserId ? (
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">You</span>
                  ) : (
                    <button data-testid={`btn-delete-admin-${a.user_id}`} onClick={() => handleDeleteAdmin(a.user_id)} className="text-zinc-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : tab === "invites" ? (
            <div className="space-y-2" data-testid="invite-list">
              {lastCreated && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-2 space-y-2" data-testid="invite-created-banner">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-emerald-900">
                      Invitation created for {lastCreated.email}
                    </div>
                    <button
                      onClick={() => setLastCreated(null)}
                      className="text-emerald-700 hover:text-emerald-900 text-xs"
                      aria-label="Dismiss"
                    >×</button>
                  </div>
                  {lastCreated.email_sent ? (
                    <div className="text-[11px] text-emerald-800">
                      Email sent via SendGrid. They'll receive the invitation shortly.
                    </div>
                  ) : (
                    <div className="text-[11px] text-emerald-900">
                      <strong>Email not delivered yet</strong> — no SendGrid API key configured.
                      Copy this link and send it to them manually:
                    </div>
                  )}
                  {lastCreated.invite_link && (
                    <div className="flex items-center gap-2">
                      <code className="flex-1 truncate text-[10px] bg-white border border-emerald-200 rounded px-2 py-1 text-zinc-700 font-mono" data-testid="invite-created-link">
                        {lastCreated.invite_link}
                      </code>
                      <button
                        onClick={() => copyLink(lastCreated.invite_link)}
                        className="text-[11px] px-2.5 py-1 bg-emerald-900 text-white rounded hover:bg-emerald-950 inline-flex items-center gap-1"
                        data-testid="btn-copy-invite-link"
                      >
                        {copiedToken === lastCreated.invite_link ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {invites.length === 0 ? <p className="text-sm text-zinc-500 py-4">No invitations sent yet.</p> :
                invites.map(i => (
                  <div key={i.invite_id} data-testid={`invite-row-${i.invite_id}`}
                    className="flex items-center justify-between bg-zinc-50 rounded-xl px-4 py-3 gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-900 text-sm truncate">{i.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{i.email}</div>
                      {i.invite_link && i.status === "pending" && (
                        <div className="text-[10px] text-zinc-400 mt-1">
                          Invited by {i.invited_by_name || "admin"} · expires {i.expires_at ? new Date(i.expires_at).toLocaleDateString() : ""}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        i.status === "pending" ? "bg-amber-100 text-amber-700"
                          : i.status === "accepted" ? "bg-emerald-100 text-emerald-700"
                            : "bg-zinc-200 text-zinc-500"}`}>{i.status}</span>
                      {i.status === "pending" && (
                        <>
                          <button onClick={() => resendInvite(i.invite_id)} data-testid={`btn-resend-${i.invite_id}`} title="Resend email" className="p-1.5 text-zinc-600 hover:bg-zinc-100 rounded-lg"><RotateCw size={13} /></button>
                          <button onClick={() => revokeInvite(i.invite_id)} data-testid={`btn-revoke-${i.invite_id}`} title="Revoke" className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={13} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          ) : (
            <form onSubmit={sendInvite} className="space-y-3" data-testid="form-invite-admin">
              <p className="text-sm text-zinc-600">Self-signup is disabled. Invite a specific person by email; they set their own password when they accept.</p>
              <input data-testid="input-invite-name" type="text" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              <input data-testid="input-invite-email" type="email" placeholder="email@fastprototype.ai" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="invite-error">{error}</div>}
              <button type="submit" disabled={adding} data-testid="btn-send-invite"
                className="w-full py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-xl hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {adding ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send invitation</>}
              </button>
              <p className="text-[11px] text-zinc-400 text-center">Invitees get a secure link valid for 7 days.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
